import { isAxiosError } from "helpers/utils.helper"
import { CHANGE_OWNER_MESSAGES } from "helpers/drawingOwnerChange.constants"
import type { TUser } from "types/user.type"

// The job reported `failed`; carries the backend's `error_message`.
export class OwnerChangeFailedError extends Error {}

// Polling outlasted CHANGE_OWNER_POLL_TIMEOUT_MS without a terminal state.
export class OwnerChangePollTimeoutError extends Error {}

export type TOwnerOption = { value: number; label: string }

export const formatUserName = (user: Pick<TUser, "name" | "secondName">) =>
	[user.name, user.secondName].filter(Boolean).join(" ")

// The new owner must be active and not already own the drawing; sorted by name for the picker.
export const buildOwnerOptions = (users: TUser[] | undefined, currentOwnerId: number | null | undefined) =>
	(users ?? [])
		.filter((user) => user.is_active !== false && user.id !== currentOwnerId)
		.map<TOwnerOption>((user) => ({ value: user.id, label: formatUserName(user) || user.email || `User ${user.id}` }))
		.sort((a, b) => a.label.localeCompare(b.label))

const firstMessage = (value: unknown): string | null => {
	if (typeof value === "string" && value) return value
	if (Array.isArray(value) && typeof value[0] === "string" && value[0]) return value[0]
	return null
}

// 400 and 409 carry a specific reason from the backend (bad new owner / busy drawing), so it is shown as-is.
export const getOwnerChangeErrorMessage = (error: unknown): string => {
	if (error instanceof OwnerChangePollTimeoutError) return CHANGE_OWNER_MESSAGES.timedOut
	if (error instanceof OwnerChangeFailedError) return error.message || CHANGE_OWNER_MESSAGES.failed
	if (!isAxiosError(error)) return CHANGE_OWNER_MESSAGES.failed

	const data = error.response?.data as Record<string, unknown> | undefined
	switch (error.response?.status) {
		case 400:
			return firstMessage(data?.new_owner_id) ?? firstMessage(data?.detail) ?? CHANGE_OWNER_MESSAGES.failed
		case 401:
			return CHANGE_OWNER_MESSAGES.unauthorized
		case 403:
			return CHANGE_OWNER_MESSAGES.forbidden
		case 404:
			return CHANGE_OWNER_MESSAGES.notFound
		case 409:
			return firstMessage(data?.detail) ?? CHANGE_OWNER_MESSAGES.busy
		default:
			return CHANGE_OWNER_MESSAGES.failed
	}
}
